import { useEffect, useMemo, useRef, useState } from 'react';
import { Filter, ChevronDown, ChevronRight, Users, Plus, Trash2, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { planningApi } from './api';
import { PlanningPhase, PlanningAssignment, PlanningResource, PlanningResourceNeed, PHASE_STATUS_LABEL } from './types';
import { addDaysISO, toISO, VISIBLE_DAYS, DAY_PX, GRID_WIDTH, monthGroupsFor, dayBarStyle } from './dayGrid';
import { coverageBadge } from './resourceCoverage';

function groupByPhaseId<T extends { phaseId: string }>(list: T[]): Record<string, T[]> {
  const map: Record<string, T[]> = {};
  for (const item of list) {
    if (!map[item.phaseId]) map[item.phaseId] = [];
    map[item.phaseId].push(item);
  }
  return map;
}

const STATUS_COLOR: Record<string, string> = {
  PLANIFICADA: '#94a3b8',
  EN_CURS: '#2563eb',
  COMPLETADA: '#16a34a',
  CANCELLADA: '#dc2626',
};

const SENSE_CAPITOL = 'Sense capítol';

// Color suau i determinista per expedient (mateix expedient = mateix color
// sempre), perquè les barres resum deixin de ser totes grises i es puguin
// distingir d'un cop d'ull sense arribar a semblar un arc de Sant Martí.
const EXPEDIENT_COLORS = [
  { bar: 'bg-blue-400', chapterBar: 'bg-blue-300' },
  { bar: 'bg-violet-400', chapterBar: 'bg-violet-300' },
  { bar: 'bg-teal-400', chapterBar: 'bg-teal-300' },
  { bar: 'bg-amber-400', chapterBar: 'bg-amber-300' },
  { bar: 'bg-rose-400', chapterBar: 'bg-rose-300' },
  { bar: 'bg-cyan-400', chapterBar: 'bg-cyan-300' },
];
function colorForExpedient(key: string): { bar: string; chapterBar: string } {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return EXPEDIENT_COLORS[hash % EXPEDIENT_COLORS.length];
}

function formatHours(hours: number): string {
  const n = Number(hours);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

type Row =
  | { kind: 'expedient'; key: string; label: string; range: { start: string; end: string } | null }
  | { kind: 'chapter'; key: string; label: string; expedientKey: string; range: { start: string; end: string } | null }
  | { kind: 'phase'; key: string; phase: PlanningPhase; expedientKey: string; chapterKey: string }
  | { kind: 'resource-sub'; key: string; phaseId: string; expedientKey: string; chapterKey: string; assignment: PlanningAssignment };

function rangeOfPhases(list: PlanningPhase[]): { start: string; end: string } | null {
  const planned = list.filter((p) => p.startDate && p.endDate);
  if (planned.length === 0) return null;
  const starts = planned.map((p) => p.startDate as string).sort();
  const ends = planned.map((p) => p.endDate as string).sort();
  return { start: starts[0], end: ends[ends.length - 1] };
}

export default function GeneralTab() {
  const [phases, setPhases] = useState<PlanningPhase[]>([]);
  const [chapterOrders, setChapterOrders] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [rangeStart, setRangeStart] = useState(toISO(new Date()));
  const [collapsedExpedients, setCollapsedExpedients] = useState<Set<string>>(new Set());
  const [collapsedChapters, setCollapsedChapters] = useState<Set<string>>(new Set());
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [assignmentsByPhaseId, setAssignmentsByPhaseId] = useState<Record<string, PlanningAssignment[]>>({});
  const [needsByPhaseId, setNeedsByPhaseId] = useState<Record<string, PlanningResourceNeed[]>>({});
  const [resources, setResources] = useState<PlanningResource[]>([]);
  const resourceNameById = useMemo(() => Object.fromEntries(resources.map((r) => [r.id, r.name])), [resources]);

  // Panel lateral d'assignació de recursos per a la fase seleccionada
  // (mateix patró que ScheduleTab.tsx, pero disponible des de la vista
  // creuada perquè es pot veure tota la resta de necessitats/assignacions
  // d'un cop d'ull sense haver de cercar l'expedient al Cronograma).
  const [selectedPhase, setSelectedPhase] = useState<PlanningPhase | null>(null);
  const [needs, setNeeds] = useState<PlanningResourceNeed[]>([]);
  const [needForm, setNeedForm] = useState<{ profile: string; quantity: number }>({ profile: '', quantity: 1 });
  const [assignments, setAssignments] = useState<PlanningAssignment[]>([]);
  const [assignForm, setAssignForm] = useState<{ resourceId: string; needId?: string; startDate: string; endDate: string; startTime: string; endTime: string }>({ resourceId: '', startDate: '', endDate: '', startTime: '', endTime: '' });
  const [conflicts, setConflicts] = useState<{ type: string; message: string }[] | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const toggleExpedient = (key: string) => setCollapsedExpedients((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleChapter = (key: string) => setCollapsedChapters((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const togglePhaseExpand = (key: string) => setExpandedPhases((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  useEffect(() => {
    planningApi.listResources(undefined, 'active').then(setResources);
  }, []);

  // Al primer accés a la pestanya, els expedients han d'aparèixer col·lapsats
  // (nomes el títol visible) perquè no aclapari amb totes les fases obertes.
  const didAutoCollapse = useRef(false);

  const load = () => {
    setLoading(true);
    planningApi.listPhases({ status: status || undefined, search: search || undefined }).then(async (data) => {
      setPhases(data);
      const dates = data.map((p) => p.startDate).filter((d): d is string => !!d).sort();
      if (dates.length > 0) setRangeStart((prev) => (prev > dates[0] ? dates[0] : prev));

      if (!didAutoCollapse.current && data.length > 0) {
        didAutoCollapse.current = true;
        const expedientKeys = new Set(data.map((p) => `exp-${p.expedientCode || p.expedientLabel || '(sense expedient)'}`));
        setCollapsedExpedients(expedientKeys);
      }

      // L'ordre de capítols es propi de cada expedient (planning_expedient_
      // chapter_orders) — cal consultar-lo per cada expedient present.
      const codes = Array.from(new Set(data.map((p) => p.expedientCode).filter((c): c is string => !!c)));
      const orders = await Promise.all(codes.map((c) => planningApi.getChapterOrder(c)));
      const map: Record<string, string[]> = {};
      codes.forEach((c, i) => { map[c] = orders[i]; });
      setChapterOrders(map);

      const phaseIds = data.map((p) => p.id);
      const [allAssignments, allNeeds] = await Promise.all([planningApi.listAssignments({ phaseIds }), planningApi.listNeedsBulk(phaseIds)]);
      setAssignmentsByPhaseId(groupByPhaseId(allAssignments));
      setNeedsByPhaseId(groupByPhaseId(allNeeds));
    }).finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  // Força una sincronització de totes les fases des del Cronograma de cada
  // expedient (backfill inicial o reparació manual — vegeu
  // planningSync.service.ts). Normalment no cal: la sincronització ja es
  // dispara sola en editar el Cronograma de l'expedient.
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await planningApi.syncAllFromCronograma();
      load();
    } finally {
      setSyncing(false);
    }
  };

  const openPhase = async (p: PlanningPhase) => {
    setSelectedPhase(p);
    const [n, a] = await Promise.all([planningApi.listNeeds(p.id), planningApi.listAssignments({ phaseId: p.id })]);
    setNeeds(n);
    setAssignments(a);
    setConflicts(null);
    setAssignForm({ resourceId: '', startDate: '', endDate: '', startTime: '', endTime: '' });
  };

  const addNeed = async () => {
    if (!selectedPhase || !needForm.profile.trim()) return;
    await planningApi.addNeed(selectedPhase.id, needForm);
    setNeedForm({ profile: '', quantity: 1 });
    const updated = await planningApi.listNeeds(selectedPhase.id);
    setNeeds(updated);
    setNeedsByPhaseId((prev) => ({ ...prev, [selectedPhase.id]: updated }));
  };

  const removeNeed = async (id: string) => {
    if (!selectedPhase) return;
    await planningApi.removeNeed(selectedPhase.id, id);
    const updated = await planningApi.listNeeds(selectedPhase.id);
    setNeeds(updated);
    setNeedsByPhaseId((prev) => ({ ...prev, [selectedPhase.id]: updated }));
  };

  const checkConflicts = async () => {
    if (!selectedPhase || !assignForm.resourceId || !assignForm.startDate || !assignForm.endDate) return;
    const data = await planningApi.checkConflicts({
      resourceId: assignForm.resourceId, startDate: assignForm.startDate, endDate: assignForm.endDate,
      startTime: assignForm.startTime || null, endTime: assignForm.endTime || null,
    });
    setConflicts(data);
  };

  const createAssignment = async (force = false) => {
    if (!selectedPhase || !assignForm.resourceId || !assignForm.startDate || !assignForm.endDate) return;
    try {
      await planningApi.createAssignment({
        resourceId: assignForm.resourceId, phaseId: selectedPhase.id, resourceNeedId: assignForm.needId || null,
        startDate: assignForm.startDate, endDate: assignForm.endDate,
        startTime: assignForm.startTime || null, endTime: assignForm.endTime || null,
        conflictOverride: force, conflictOverrideReason: force ? overrideReason : undefined,
      });
      setAssignForm({ resourceId: '', startDate: '', endDate: '', startTime: '', endTime: '' });
      setConflicts(null);
      setOverrideReason('');
      const updated = await planningApi.listAssignments({ phaseId: selectedPhase.id });
      setAssignments(updated);
      setAssignmentsByPhaseId((prev) => ({ ...prev, [selectedPhase.id]: updated }));
    } catch (e: any) {
      alert(e?.response?.data?.error?.message ?? 'No s\'ha pogut crear l\'assignació');
    }
  };

  const confirmAssignment = async (a: PlanningAssignment) => {
    await planningApi.updateAssignment(a.id, { status: 'CONFIRMADA' });
    if (selectedPhase) {
      const updated = await planningApi.listAssignments({ phaseId: selectedPhase.id });
      setAssignments(updated);
      setAssignmentsByPhaseId((prev) => ({ ...prev, [selectedPhase.id]: updated }));
    }
  };

  const removeAssignment = async (a: PlanningAssignment) => {
    if (!confirm('Eliminar aquesta assignació?')) return;
    await planningApi.removeAssignment(a.id);
    if (selectedPhase) {
      const updated = await planningApi.listAssignments({ phaseId: selectedPhase.id });
      setAssignments(updated);
      setAssignmentsByPhaseId((prev) => ({ ...prev, [selectedPhase.id]: updated }));
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<string, PlanningPhase[]>();
    for (const p of phases) {
      const key = p.expedientCode || p.expedientLabel || '(sense expedient)';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [phases]);

  // Una fila plana per expedient/capítol/fase — es renderitza igual a la
  // columna esquerra i a la graella de dies, perquè les files quedin alineades.
  const rows = useMemo(() => {
    const out: Row[] = [];
    grouped.forEach(([expedient, list]) => {
      const expedientKey = `exp-${expedient}`;
      const label = list[0]?.expedientCode ? list[0]?.expedientLabel : null;
      out.push({ kind: 'expedient', key: expedientKey, label: label ? `${expedient} — ${label}` : expedient, range: rangeOfPhases(list) });
      const order = chapterOrders[expedient] ?? [];
      const byChapter = new Map<string, PlanningPhase[]>();
      list.forEach((p) => {
        const key = p.chapter?.trim() || SENSE_CAPITOL;
        if (!byChapter.has(key)) byChapter.set(key, []);
        byChapter.get(key)!.push(p);
      });
      const names = Array.from(byChapter.keys()).filter((c) => c !== SENSE_CAPITOL);
      const ordered = [...order.filter((c) => byChapter.has(c)), ...names.filter((c) => !order.includes(c))];
      if (byChapter.has(SENSE_CAPITOL)) ordered.push(SENSE_CAPITOL);
      ordered.forEach((chapter) => {
        const chapterKey = `${expedient}-ch-${chapter}`;
        out.push({ kind: 'chapter', key: chapterKey, label: chapter, expedientKey, range: rangeOfPhases(byChapter.get(chapter)!) });
        byChapter.get(chapter)!.forEach((p) => {
          out.push({ kind: 'phase', key: p.id, phase: p, expedientKey, chapterKey });
          (assignmentsByPhaseId[p.id] ?? []).forEach((a) => out.push({ kind: 'resource-sub', key: `${p.id}-a-${a.id}`, phaseId: p.id, expedientKey, chapterKey, assignment: a }));
        });
      });
    });
    return out;
  }, [grouped, chapterOrders, assignmentsByPhaseId]);

  // Amaga les files de capítols/fases d'un expedient col·lapsat, les de
  // fases d'un capítol col·lapsat, i les de recursos d'una fase no desplegada
  // (mantenint les capçaleres visibles).
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (row.kind === 'expedient') return true;
    if (collapsedExpedients.has(row.expedientKey)) return false;
    if (row.kind === 'chapter') return true;
    if (collapsedChapters.has(row.chapterKey)) return false;
    if (row.kind === 'phase') return true;
    return expandedPhases.has(row.phaseId);
  }), [rows, collapsedExpedients, collapsedChapters, expandedPhases]);

  const todayIso = toISO(new Date());
  const monthGroups = monthGroupsFor(rangeStart);

  const DayGridLines = () => (
    <div className="absolute inset-0 flex pointer-events-none" style={{ width: GRID_WIDTH }}>
      {Array.from({ length: VISIBLE_DAYS }).map((_, di) => {
        const dayIso = addDaysISO(rangeStart, di);
        const d = new Date(`${dayIso}T00:00:00`);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const isToday = dayIso === todayIso;
        return <div key={di} className={`shrink-0 border-r ${isToday ? 'bg-amber-50 border-amber-200' : `border-gray-100 ${isWeekend ? 'bg-gray-50' : ''}`}`} style={{ width: DAY_PX }} />;
      })}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
    <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <Filter size={14} className="text-muted-foreground" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1.5">
          <option value="">Tots els estats</option>
          {Object.entries(PHASE_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input
          placeholder="Cercar per nom, expedient..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5"
        />
        <button onClick={load} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">Filtrar</button>
        <button
          onClick={handleSyncAll}
          disabled={syncing}
          title="Torna a sincronitzar totes les fases des del Cronograma de cada expedient"
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Sincronitzant...' : 'Sincronitza'}
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-xs text-muted-foreground">Carregant...</div>
      ) : phases.length === 0 ? (
        <div className="p-8 text-center text-xs text-muted-foreground">Sense fases que coincideixin amb el filtre</div>
      ) : (
        <div>
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-[11px] text-muted-foreground">
              {new Date(`${rangeStart}T00:00:00`).toLocaleDateString('ca-ES')} — {new Date(`${addDaysISO(rangeStart, VISIBLE_DAYS - 1)}T00:00:00`).toLocaleDateString('ca-ES')}
            </span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setRangeStart(addDaysISO(rangeStart, -7))} className="text-[11px] px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">‹ setmana</button>
              <button onClick={() => setRangeStart(addDaysISO(rangeStart, 7))} className="text-[11px] px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">setmana ›</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="flex" style={{ minWidth: `${420 + GRID_WIDTH}px` }}>
              {/* Esquerra: expedient + nom de la fase */}
              <div className="w-[420px] shrink-0 border-r border-gray-200">
                <div className="h-10 border-b border-gray-200 bg-gray-50" />
                {visibleRows.map((row) => {
                  if (row.kind === 'expedient') {
                    const collapsed = collapsedExpedients.has(row.key);
                    return (
                      <button key={row.key} type="button" onClick={() => toggleExpedient(row.key)} className="w-full flex items-center gap-1 h-8 px-2 border-b border-gray-200 bg-gray-50 hover:bg-gray-100 text-left">
                        {collapsed ? <ChevronRight size={13} className="text-gray-400 shrink-0" /> : <ChevronDown size={13} className="text-gray-400 shrink-0" />}
                        <span className="text-xs font-semibold text-gray-700 truncate">{row.label}</span>
                      </button>
                    );
                  }
                  if (row.kind === 'chapter') {
                    const collapsed = collapsedChapters.has(row.key);
                    return (
                      <button key={row.key} type="button" onClick={() => toggleChapter(row.key)} className="w-full flex items-center gap-1 h-6 px-2 pl-4 border-b border-gray-100 bg-gray-50/60 hover:bg-gray-100 text-left">
                        {collapsed ? <ChevronRight size={11} className="text-muted-foreground shrink-0" /> : <ChevronDown size={11} className="text-muted-foreground shrink-0" />}
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide truncate">{row.label}</span>
                      </button>
                    );
                  }
                  if (row.kind === 'phase') {
                    const p = row.phase;
                    const assigns = assignmentsByPhaseId[p.id] ?? [];
                    const needs = needsByPhaseId[p.id] ?? [];
                    const badge = coverageBadge(needs, assigns);
                    const expanded = expandedPhases.has(p.id);
                    return (
                      <div key={row.key} onClick={() => openPhase(p)} className={`flex items-center h-8 px-2 pl-7 border-b border-gray-100 text-xs gap-1 cursor-pointer ${selectedPhase?.id === p.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        {assigns.length > 0 ? (
                          <button type="button" title="Mostrar/amagar recursos assignats" onClick={(e) => { e.stopPropagation(); togglePhaseExpand(p.id); }} className="p-0.5 rounded hover:bg-gray-200 shrink-0">
                            {expanded ? <ChevronDown size={11} className="text-gray-400" /> : <ChevronRight size={11} className="text-gray-400" />}
                          </button>
                        ) : <span className="w-[18px] shrink-0" />}
                        <span className="truncate flex-1">{p.name}</span>
                        {Number(p.plannedHours) > 0 && <span title="Hores (amidament)" className="text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 bg-violet-50 text-violet-700">{formatHours(p.plannedHours)}h</span>}
                        {badge && <span title="Cobertura de recursos" className={`text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 flex items-center gap-0.5 ${badge.className}`}><Users size={9} />{badge.text}</span>}
                        {!p.startDate && <span className="text-[10px] text-muted-foreground shrink-0">sense planificar</span>}
                      </div>
                    );
                  }
                  return (
                    <div key={row.key} className="flex items-center h-6 px-2 pl-12 border-b border-gray-50 text-[11px] text-muted-foreground gap-1">
                      <span className="truncate flex-1">{resourceNameById[row.assignment.resourceId] ?? '—'}</span>
                      <span className={`text-[9px] px-1 rounded shrink-0 ${row.assignment.status === 'CONFIRMADA' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{row.assignment.status === 'CONFIRMADA' ? 'OK' : 'Prov.'}</span>
                    </div>
                  );
                })}
              </div>

              {/* Dreta: graella de dies */}
              <div className="shrink-0 relative" style={{ width: GRID_WIDTH }}>
                <div className="flex h-5 border-b border-gray-200 bg-gray-50" style={{ width: GRID_WIDTH }}>
                  {monthGroups.map((mg, mi) => (
                    <div key={mi} className="shrink-0 border-r border-gray-200 flex items-center justify-center text-[10px] font-semibold text-muted-foreground uppercase" style={{ width: mg.days * DAY_PX }}>{mg.label}</div>
                  ))}
                </div>
                <div className="flex h-5 border-b border-gray-200 bg-gray-50" style={{ width: GRID_WIDTH }}>
                  {Array.from({ length: VISIBLE_DAYS }).map((_, di) => {
                    const dayIso = addDaysISO(rangeStart, di);
                    const d = new Date(`${dayIso}T00:00:00`);
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const isToday = dayIso === todayIso;
                    return (
                      <div key={di} className={`shrink-0 border-r flex items-center justify-center text-[9px] font-semibold ${isToday ? 'bg-amber-200 border-amber-300 text-amber-900' : `border-gray-100 font-normal ${isWeekend ? 'bg-gray-100 text-muted-foreground/60' : 'text-muted-foreground'}`}`} style={{ width: DAY_PX }}>
                        {d.getDate()}
                      </div>
                    );
                  })}
                </div>

                {visibleRows.map((row) => {
                  if (row.kind === 'expedient') {
                    const color = colorForExpedient(row.key);
                    return (
                      <div key={row.key} className="relative h-8 border-b border-gray-200" style={{ width: GRID_WIDTH }}>
                        <DayGridLines />
                        {row.range && <div className={`absolute top-1.5 bottom-1.5 rounded ${color.bar} opacity-80`} style={dayBarStyle(row.range.start, row.range.end, rangeStart)} title={`${row.label}: ${row.range.start} — ${row.range.end}`} />}
                      </div>
                    );
                  }
                  if (row.kind === 'chapter') {
                    const color = colorForExpedient(row.expedientKey);
                    return (
                      <div key={row.key} className="relative h-6 border-b border-gray-100" style={{ width: GRID_WIDTH }}>
                        <DayGridLines />
                        {row.range && <div className={`absolute top-1 bottom-1 rounded ${color.chapterBar} opacity-80`} style={dayBarStyle(row.range.start, row.range.end, rangeStart)} title={`${row.label}: ${row.range.start} — ${row.range.end}`} />}
                      </div>
                    );
                  }
                  if (row.kind === 'phase') {
                    const p = row.phase;
                    return (
                      <div key={row.key} className="relative h-8 border-b border-gray-100" style={{ width: GRID_WIDTH }}>
                        <DayGridLines />
                        {p.startDate && p.endDate && (
                          <div
                            className="absolute top-1 bottom-1 rounded flex items-center"
                            style={{ ...dayBarStyle(p.startDate, p.endDate, rangeStart), backgroundColor: STATUS_COLOR[p.status] }}
                            title={`${p.name}: ${p.startDate} — ${p.endDate}`}
                          />
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={row.key} className="relative h-6 border-b border-gray-50" style={{ width: GRID_WIDTH }}>
                      <DayGridLines />
                      <div
                        className={`absolute top-1 bottom-1 rounded ${row.assignment.status === 'CONFIRMADA' ? 'bg-blue-600' : 'bg-blue-300'}`}
                        style={dayBarStyle(row.assignment.startDate, row.assignment.endDate, rangeStart)}
                        title={`${resourceNameById[row.assignment.resourceId] ?? ''}: ${row.assignment.startDate} — ${row.assignment.endDate}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    <div className="lg:col-span-1 bg-white border border-gray-200 rounded-lg">
      {!selectedPhase ? (
        <div className="p-6 text-center text-xs text-muted-foreground">Selecciona una fase a l'esquerra per gestionar les seves necessitats i assignacions de recurs</div>
      ) : (
        <div>
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">{selectedPhase.name}</h3>
            <p className="text-[11px] text-muted-foreground">
              {selectedPhase.expedientCode && <>{selectedPhase.expedientCode}{selectedPhase.expedientLabel ? ` — ${selectedPhase.expedientLabel}` : ''} · </>}
              {selectedPhase.startDate && selectedPhase.endDate ? <>Vigent: {selectedPhase.startDate} a {selectedPhase.endDate}</> : 'Encara sense planificar'}
            </p>
          </div>

          <div className="p-4 border-b border-gray-100">
            <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1.5"><Users size={13} /> Necessitats de recurs</h4>
            <div className="space-y-1 mb-2">
              {needs.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">Sense necessitats definides</p>
              ) : needs.map((n) => {
                const covered = assignments.filter((a) => a.resourceNeedId === n.id).length;
                return (
                  <div key={n.id} className="flex items-center justify-between text-xs">
                    <span>{n.profile} — {covered}/{n.quantity} coberts{n.dedicationHours ? ` · ${n.dedicationHours}h` : ''}</span>
                    <button onClick={() => removeNeed(n.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 size={12} /></button>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-1.5">
              <input placeholder="Perfil (p.ex. Operari pavimento)" value={needForm.profile} onChange={(e) => setNeedForm((f) => ({ ...f, profile: e.target.value }))} className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
              <input type="number" min={1} value={needForm.quantity} onChange={(e) => setNeedForm((f) => ({ ...f, quantity: Number(e.target.value) }))} className="w-14 text-xs border border-gray-200 rounded px-2 py-1" />
              <button onClick={addNeed} className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"><Plus size={13} /></button>
            </div>
          </div>

          <div className="p-4">
            <h4 className="text-xs font-semibold text-gray-900 mb-2">Assignacions</h4>
            <div className="space-y-1.5 mb-3">
              {assignments.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">Sense assignacions</p>
              ) : assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-xs border border-gray-100 rounded px-2 py-1.5">
                  <div>
                    <span className="font-medium">{resourceNameById[a.resourceId] ?? '—'}</span>
                    <span className="text-muted-foreground"> · {a.startDate}{a.startTime ? ` ${a.startTime}` : ''} a {a.endDate}{a.endTime ? ` ${a.endTime}` : ''}</span>
                    <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${a.status === 'CONFIRMADA' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{a.status === 'CONFIRMADA' ? 'Confirmada' : 'Provisional'}</span>
                    {!!a.conflictOverride && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700">Excepció autoritzada</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    {a.status !== 'CONFIRMADA' && <button onClick={() => confirmAssignment(a)} title="Confirmar" className="p-1 hover:bg-green-100 rounded text-green-700"><CheckCircle2 size={13} /></button>}
                    <button onClick={() => removeAssignment(a)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1.5 bg-gray-50 rounded p-2.5">
              <select value={assignForm.resourceId} onChange={(e) => { setAssignForm((f) => ({ ...f, resourceId: e.target.value })); setConflicts(null); }} className="w-full text-xs border border-gray-200 rounded px-2 py-1.5">
                <option value="">Selecciona un recurs...</option>
                {resources.map((r) => <option key={r.id} value={r.id}>{r.name}{r.profile ? ` (${r.profile})` : ''}</option>)}
              </select>
              {needs.length > 0 && (
                <select value={assignForm.needId ?? ''} onChange={(e) => setAssignForm((f) => ({ ...f, needId: e.target.value || undefined }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1.5">
                  <option value="">Sense vincular a cap necessitat</option>
                  {needs.map((n) => <option key={n.id} value={n.id}>{n.profile}</option>)}
                </select>
              )}
              <div className="grid grid-cols-2 gap-1.5">
                <input type="date" value={assignForm.startDate} onChange={(e) => { setAssignForm((f) => ({ ...f, startDate: e.target.value })); setConflicts(null); }} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <input type="date" value={assignForm.endDate} onChange={(e) => { setAssignForm((f) => ({ ...f, endDate: e.target.value })); setConflicts(null); }} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <input type="time" placeholder="Hora inici (opc.)" value={assignForm.startTime} onChange={(e) => { setAssignForm((f) => ({ ...f, startTime: e.target.value })); setConflicts(null); }} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
                <input type="time" placeholder="Hora fi (opc.)" value={assignForm.endTime} onChange={(e) => { setAssignForm((f) => ({ ...f, endTime: e.target.value })); setConflicts(null); }} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
              </div>

              {conflicts && conflicts.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-2 space-y-1">
                  {conflicts.map((c, i) => (
                    <p key={i} className="text-[11px] text-red-700 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {c.message}</p>
                  ))}
                  <input placeholder="Motiu de l'excepció (obligatori per forçar)" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="w-full text-[11px] border border-red-200 rounded px-2 py-1 mt-1" />
                  <button disabled={!overrideReason.trim()} onClick={() => createAssignment(true)} className="w-full text-[11px] px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50">Confirmar excepció i assignar igualment</button>
                </div>
              )}
              {conflicts && conflicts.length === 0 && (
                <p className="text-[11px] text-green-700 flex items-center gap-1"><CheckCircle2 size={12} /> Sense conflictes</p>
              )}

              <div className="flex items-center gap-1.5">
                <button onClick={checkConflicts} className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded hover:bg-gray-100">Comprovar conflictes</button>
                <button onClick={() => createAssignment(false)} className="flex-1 text-xs px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">Assignar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
